// Accounts department dashboard, laid out like the HR dashboard. Tiles come from
// GetAccountsDashboardSummary (spGetAccountsDashboardSummary.sql) and drill into
// PendingDocumentsModal via GetAccountsPendingDetails (spGetAccountsPendingDetails.sql): pending
// bank transactions / vendor invoices show the level + role they are pending with, FDs / LCs show
// their due date. Cash balance is GetCCCashBalanceSummary (non-zero CC balances only).
// Frequent Access and Recent Transactions reuse the shared cards every dashboard uses.
import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Wallet, Landmark, FileText, PiggyBank, ScrollText, Loader2 } from 'lucide-react';
import FrequentAccessCard from './FrequentAccessCard';
import ApprovalsCard from './ApprovalsCard';
import RecentTransactionsCard from './RecentTransactionsCard';
import { MiniStatTile } from './StatTile';
import NeedsAttentionCard from './NeedsAttentionCard';
import PendingDocumentsModal, { COLUMNS } from './PendingDocumentsModal';
import { getAccountsPendingDetails } from '../../api/AccountsAPI/accountsDashboardAPI';
import {
    fetchCCCashBalanceSummary,
    fetchAccountsDashboardSummary,
    selectCCCashBalances,
    selectAccountsDashboardLoading,
    selectAccountsDashboardSummary,
    selectAccountsDashboardSummaryLoading,
} from '../../slices/dashboardSlice/accountsDashboardSlice';
import { formatCurrency } from '../../utilities/formatCurrency';

// Modal title + columns per drill-down Type. Bank / VendorInvoice get the Level / Pending With
// columns and level chips from the modal; FD / LC are already approved, so they show due dates.
const ACCOUNTS_DOC_CONFIG = {
    Bank: {
        title: 'Bank Transactions Under Process',
        noun: 'bank transaction',
        columns: [
            COLUMNS.docNo('Ref No'),
            { ...COLUMNS.docType, label: 'Transaction Type' },
            COLUMNS.party('Bank'),
            COLUMNS.date('Transaction Date'),
            COLUMNS.amount,
            COLUMNS.remarks,
        ],
    },
    VendorInvoice: {
        title: 'Vendor Invoices Under Process',
        noun: 'vendor invoice',
        columns: [
            COLUMNS.docNo('Invoice No'),
            COLUMNS.refNo('PO No'),
            { ...COLUMNS.docType, label: 'Vendor Type' },
            COLUMNS.party('Vendor'),
            COLUMNS.cc,
            COLUMNS.date('Invoice Date'),
            COLUMNS.amount,
        ],
    },
    FDEnd: {
        title: 'FDs Nearing End Date',
        noun: 'FD',
        summary: 'ending in the next 30 days',
        showLevels: false,
        columns: [
            COLUMNS.docNo('FDR No'),
            COLUMNS.party('Bank'),
            COLUMNS.date('Begin Date'),
            { ...COLUMNS.amount, label: 'FD Value (₹)' },
            { ...COLUMNS.remarks, label: 'Rate' },
            { ...COLUMNS.expiry, label: 'End Date' },
            COLUMNS.daysLeft,
        ],
    },
    LCDue: {
        title: 'LCs Nearing Payment',
        noun: 'LC transaction',
        summary: 'valid up to the next 30 days',
        showLevels: false,
        columns: [
            COLUMNS.docNo('LC No'),
            COLUMNS.refNo('Transaction Ref No'),
            COLUMNS.party('Vendor'),
            COLUMNS.date('Issue Date'),
            { ...COLUMNS.amount, label: 'Balance (₹)' },
            { ...COLUMNS.expiry, label: 'Valid Up To' },
            COLUMNS.daysLeft,
        ],
    },
};

// Rows for the "Needs attention" panel — approved FDs / LCs coming due
const NEEDS_ATTENTION_ROWS = [
    { key: 'FDEnd', countKey: 'FDNearingEndCount', title: 'FDs nearing end date', subtitle: 'Open fixed deposits reaching their end date', icon: PiggyBank, tone: 'purple', dueLabel: 'Next 30 days' },
    { key: 'LCDue', countKey: 'LCNearingDueCount', title: 'LCs nearing payment', subtitle: 'Unsettled LC transactions reaching their validity date', icon: ScrollText, tone: 'red', dueLabel: 'Next 30 days' },
];

const CCCashBalanceCard = ({ balances, loading }) => (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex items-start justify-between mb-1">
            <div>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Cash balance by cost center</h3>
                <p className="text-xs text-gray-400 dark:text-gray-500">Cost centers currently holding cash</p>
            </div>
            <span className="text-[11px] font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 px-2 py-1 rounded-full whitespace-nowrap flex-shrink-0">
                {loading ? '…' : `${balances.length} cost center${balances.length === 1 ? '' : 's'}`}
            </span>
        </div>

        <div className="max-h-64 overflow-y-auto mt-2">
            {loading ? (
                <p className="text-xs text-gray-400 dark:text-gray-500 py-6 text-center">Loading…</p>
            ) : balances.length === 0 ? (
                <p className="text-xs text-gray-400 dark:text-gray-500 py-6 text-center">No cash held in any cost center</p>
            ) : (
                <ul className="divide-y divide-gray-100 dark:divide-gray-700">
                    {balances.map((cc) => (
                        <li key={cc.CCCode} className="flex items-center justify-between py-2 text-sm">
                            <span className="text-gray-700 dark:text-gray-300 truncate" title={`${cc.CCCode} - ${cc.CCName}`}>
                                <span className="font-medium text-gray-900 dark:text-white">{cc.CCCode}</span>
                                <span className="text-gray-400 dark:text-gray-500"> · {cc.CCName}</span>
                            </span>
                            <span className={`tabular-nums font-semibold ml-2 flex-shrink-0 ${cc.CCAmount < 0 ? 'text-red-600 dark:text-red-500' : 'text-gray-900 dark:text-white'}`}>
                                ₹{formatCurrency(cc.CCAmount)}
                            </span>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    </div>
);

const AccountsDashboard = ({ onNavigate, linkFrequency, trackMenuUsage }) => {
    const { roleData, userData } = useSelector((state) => state.auth);
    const dispatch = useDispatch();

    const ccCashBalances = useSelector(selectCCCashBalances);
    const cashLoading = useSelector(selectAccountsDashboardLoading);
    const summary = useSelector(selectAccountsDashboardSummary);
    const summaryLoading = useSelector(selectAccountsDashboardSummaryLoading);

    const userId = userData?.userId || userData?.UID || userData?.employeeId || '';
    const roleId = userData?.roleId || userData?.RID || 0;

    const [detailModal, setDetailModal] = useState({ open: false, loading: false, rows: [], type: null });

    useEffect(() => {
        // roleId/userId drive backend CC-scoping: for roles with ApplicableForCC='Yes',
        // results are restricted to the cost centers assigned to this user in UserCostCenters.
        dispatch(fetchCCCashBalanceSummary({ roleId, userId }));
        dispatch(fetchAccountsDashboardSummary({ roleId, userId }));
    }, [dispatch, roleId, userId]);

    // type = 'Bank' | 'VendorInvoice' (under process, with current level/role) or 'FDEnd' | 'LCDue'
    const openDetail = async (type) => {
        setDetailModal({ open: true, loading: true, rows: [], type });
        try {
            const response = await getAccountsPendingDetails({ type, roleId, userId });
            setDetailModal({ open: true, loading: false, rows: response?.Data || [], type });
        } catch (error) {
            setDetailModal({ open: true, loading: false, rows: [], type });
        }
    };

    const cashTotal = ccCashBalances.reduce((sum, cc) => sum + (cc.CCAmount || 0), 0);
    const needsAttentionRows = NEEDS_ATTENTION_ROWS.map((row) => ({ ...row, count: summary?.[row.countKey] || 0 }));

    return (
        <div className="space-y-6">
            {/* Welcome Header */}
            <div className="bg-gradient-to-r from-[#0d1b5e] to-[#0a1240] dark:from-[#0a1240] dark:to-gray-900 rounded-lg p-6 text-white transition-colors">
                <h1 className="text-2xl font-bold mb-2">Welcome Back!</h1>
                <p className="text-orange-200 dark:text-orange-300">Employee: {userData?.firstName} | Role: {userData?.roleCode}</p>
            </div>

            {/* Stats + needs-attention + cash balance on one side, Quick access on the other */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-start">
                <div className="lg:col-span-2 space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                        {/* Cash in hand — headline tile */}
                        <div className="sm:col-span-2 bg-gradient-to-br from-[#0d1b5e] to-[#16247f] dark:from-[#0a1240] dark:to-[#111a4d] rounded-lg p-5 text-white shadow-md flex flex-col justify-between">
                            <div className="flex items-start justify-between">
                                <p className="text-sm font-medium text-blue-100">Cash in hand</p>
                                <div className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                                    <Wallet className="w-5 h-5 text-white" />
                                </div>
                            </div>
                            <div className="mt-3">
                                <p className="text-3xl font-bold">
                                    {cashLoading ? <Loader2 className="w-7 h-7 animate-spin text-white" /> : `₹${formatCurrency(cashTotal)}`}
                                </p>
                                <p className="text-xs text-blue-200 mt-1">
                                    Across {ccCashBalances.length} cost center{ccCashBalances.length === 1 ? '' : 's'}
                                </p>
                            </div>
                        </div>

                        <MiniStatTile
                            title="Bank transactions pending"
                            subtitle="Under process at all levels"
                            icon={Landmark}
                            loading={summaryLoading}
                            value={summary?.BankPendingCount}
                            onClick={summary?.BankPendingCount > 0 ? () => openDetail('Bank') : undefined}
                        />
                        <MiniStatTile
                            title="Vendor invoices pending"
                            subtitle="Under process at all levels"
                            icon={FileText}
                            loading={summaryLoading}
                            value={summary?.VendorInvoicePendingCount}
                            onClick={summary?.VendorInvoicePendingCount > 0 ? () => openDetail('VendorInvoice') : undefined}
                        />
                    </div>

                    {/* Needs attention */}
                    <NeedsAttentionCard
                        subtitle="Approved FDs and LCs coming due"
                        rows={needsAttentionRows}
                        loading={summaryLoading}
                        onOpen={openDetail}
                    />

                    <CCCashBalanceCard balances={ccCashBalances} loading={cashLoading} />
                </div>

                <div className="lg:col-span-1 space-y-4">
                    <ApprovalsCard roleId={roleId} userId={userId} pendingOnly />
                    <FrequentAccessCard
                        roleData={roleData}
                        linkFrequency={linkFrequency}
                        trackMenuUsage={trackMenuUsage}
                        onNavigate={onNavigate}
                    />
                </div>
            </div>

            {/* Recent Transactions - shared across every department dashboard */}
            <RecentTransactionsCard roleId={roleId} userId={userId} />

            <PendingDocumentsModal
                open={detailModal.open}
                onClose={() => setDetailModal({ open: false, loading: false, rows: [], type: null })}
                type={detailModal.type}
                loading={detailModal.loading}
                rows={detailModal.rows}
                configs={ACCOUNTS_DOC_CONFIG}
            />
        </div>
    );
};

export default AccountsDashboard;
